import { focusRingNoGap } from '../../Themes/base/focusRing';
import { Theme } from '../../Themes/themeProvider/ThemeProvider';
import { borderColor, borderWidth, marginSize, paddingSize } from '../../Utilities/utils/Theming';
import { IDummyProps } from './types';

export const TextareaBaseStyle = ($theme: typeof Theme, _props: IDummyProps, _isFocused: boolean) => {
	return {
		...borderWidth($theme.textarea.inputBorderWidth),
		...borderColor($theme.colors.inputBorder),
		...marginSize('0', '0', '0', '0'),
		width: '100%',
		/**
		 * Baseui v10.3.0 added the Root on the textarea and
		 * borderStyle and borderRadius where not there - see DES-1228
		 */
		borderStyle: 'solid',
		borderRadius: 'var(--epods--border-radius)',
		':hover': {
			...borderColor($theme.colors.grey700),
		},
		overflow: 'hidden',
	};
};

export const TextareaBaseFontStyle = ($theme: typeof Theme, props: IDummyProps) => {
	return {
		fontSize:
			props.size ? `var(--epods--textarea-${props.size}-font-size)` : 'var(--epods--textarea-default-font-size)',
		lineHeight:
			props.size ? `var(--epods--textarea-${props.size}-line-height-size)` : 'var(--epods--textarea-default-line-height-size)',
		fontWeight: 100,
		fontFamily: 'var(--epods--font-secondary)',
		color: $theme.textarea.textareaTextColor,
	};
};

export const TextareaEpoCustoms = (
	{
		$theme,
		$isFocused,
		$error,
		$positive,
		warning,
	}: { $theme: any; $isFocused: boolean; $error: boolean; $positive: boolean; warning: boolean },
	props: IDummyProps,
) => {
	const focusCtrl = $isFocused ? TextareaContainerFocused($theme, $isFocused) : {};
	const errorCtrl = $error ? TextareaContainerError($theme, $error) : {};
	const positiveCtrl = $positive ? TextareaContainerPositive($theme, $positive) : {};
	const readonlyCtrl = props.readOnly ? TextareaReadonlyStyles($theme) : {};
	const disabledCtrl = props.disabled ? TextareaDisabledStyles($theme) : {};
	const resizable = props.resizable ? TextareaContainerResizable : {};
	const warningCtrl = warning ? TextareaContainerWarning($theme, warning) : {};

	return {
		...focusCtrl,
		...errorCtrl,
		...positiveCtrl,
		...readonlyCtrl,
		...disabledCtrl,
		...resizable,
		...warningCtrl,
	};
};

export const TextareaDisabledInputStyles = ($theme: typeof Theme, $disabled: boolean) => {
	return {
		color: $disabled ? $theme.textarea.textareaPlaceholderColor : $theme.textarea.textareaTextColor,
		boxShadow: 'none', // it shouldn't be manipulated by the theme provider
		...borderWidth('0'), // it shouldn't be manipulated by the theme provider
		backgroundColor: 'transparent', // it shouldn't be manipulated by the theme provider
	};
};

export const TextareaInputResizable = (props: IDummyProps) => {
	// Values shouldn't be manipulated by the theme provider
	if (props.resizable) {
		return {
			minHeight: '100px',
			minWidth: '100%',
			width: '100%',
			resize: 'vertical',
		};
	}
	return {};
};

export const TextareaSpacing = ($theme: typeof Theme, props: any) => {
	const ph =
		props.size ? `var(--epods--textarea-${props.size}-padding-horizontal)` : 'var(--epods--textarea-default-padding-horizontal)';
	const pv =
		props.size ? `var(--epods--textarea-${props.size}-padding-vertical)` : 'var(--epods--textarea-default-padding-vertical)';
	return {
		...paddingSize(pv, ph, pv, ph),
	};
};

const TextareaContainerFocused = ($theme: typeof Theme, $isFocused: boolean) => {
	return {
		...focusRingNoGap,
		// we need to override the :hover so we only have the focus ring
		...borderColor('transparent !important'),
		backgroundColor: $theme.colors.inputFill,
	};
};

const TextareaContainerError = ($theme: typeof Theme, $error: boolean) => {
	return {
		...borderColor($error ? $theme.colors.inputBorderError : $theme.colors.inputBorder),
		backgroundColor: $error ? $theme.colors.inputFillError : $theme.colors.inputFill,
	};
};

const TextareaContainerPositive = ($theme: typeof Theme, $positive: boolean) => {
	return {
		...borderColor($positive ? $theme.colors.inputBorderPositive : $theme.colors.inputBorder),
		backgroundColor: $positive ? $theme.colors.inputFillPositive : $theme.colors.inputFill,
	};
};

const TextareaContainerWarning = ($theme: any, warning: boolean) => {
	return {
		...borderColor(warning ? $theme.colors.inputBorderWarning : $theme.colors.inputBorder),
		backgroundColor: warning ? $theme.colors.inputFillWarning : $theme.colors.inputFill,
	};
};

const TextareaReadonlyStyles = ($theme: typeof Theme) => {
	return {
		backgroundColor: 'var(--epods--color-bg-input-readonly)',
		':hover': {
			backgroundColor: 'var(--epods--color-bg-input-readonly)',
		},
	};
};

const TextareaDisabledStyles = ($theme: typeof Theme) => {
	return {
		...borderColor($theme.colors.inputBorder),
		...borderWidth($theme.textarea.inputBorderWidth),
		backgroundColor: $theme.colors.inputFill,
		color: 'var(--epods--color-text-disabled)',
		':hover': {
			backgroundColor: $theme.colors.inputFill,
		},
	};
};

const TextareaContainerResizable = {
	maxWidth: '100%', // it shouldn't be manipulated by the theme provider
	width: '100%'
	//width: 'min-content', // it shouldn't be manipulated by the theme provider
};
